/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useState } from 'react';
import { useCallback } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { getSmallestUrlForWidth } from '@web-stories-wp/media';

/**
 * Internal dependencies
 */
import Gallery from 'react-photo-gallery';
import MediaElement from './mediaElement';

const PHOTO_MARGIN = 4;
const FALLBACK_DIMENSION = 150;

/**
 * @callback InsertionCallback
 * @param {Object} element Inserted element.
 */

/**
 * Displays a gallery of media elements arranged in a row-based format.
 *
 * @param {Object} props Component props.
 * @param {Array.<Object>} props.resources List of resources to display.
 * @param {onDimensionsUpdate} props.onDimensionsUpdate Called when dimensions are loaded.
 * @param {InsertionCallback} props.onInsert Called when element is selected.
 * @param {string} props.providerType Provider of gallery's elements.
 * @param {boolean} props.canEditMedia Current user can upload media.
 * @return {*} The gallery element.
 */
function MediaGallery({
  resources,
  onDimensionsUpdate,
  onInsert,
  providerType,
  canEditMedia,
  isThumbnailBatchReady,
  onThumbnailReady,
}) {
  const [expandedMenuIndex, setExpandedMenuIndex] = useState(-1);

  const photos = resources.map((resource) => {
    const width =
      resource.width ||
      resource?.sizes?.web_stories_thumbnail?.width ||
      FALLBACK_DIMENSION;
    const height =
      resource.height ||
      resource?.sizes?.web_stories_thumbnail?.height ||
      FALLBACK_DIMENSION;

    return {
      src: resource.galleryOnly
        ? resource.poster || ''
        : getSmallestUrlForWidth(width, resource),
      width,
      height,
    };
  });

  const imageRenderer = useCallback(
    ({ index, photo }) => (
      <MediaElement
        key={index}
        index={index}
        isEditMenuExpanded={index === expandedMenuIndex}
        margin={PHOTO_MARGIN + 'px'}
        resource={resources[index]}
        width={photo.width}
        height={photo.height}
        onDimensionsUpdate={onDimensionsUpdate}
        onInsert={onInsert}
        providerType={providerType}
        canEditMedia={canEditMedia}
        setExpandedMenuIndex={setExpandedMenuIndex}
        isThumbnailBatchReady={isThumbnailBatchReady}
        onThumbnailReady={onThumbnailReady}
      />
    ),
    [
      providerType,
      expandedMenuIndex,
      onDimensionsUpdate,
      onInsert,
      resources,
      canEditMedia,
      isThumbnailBatchReady,
      onThumbnailReady,
    ]
  );

  return (
    <div>
      <Gallery
        targetRowHeight={110}
        direction={'row'}
        // This should match the actual margin the element is styled with.
        margin={PHOTO_MARGIN}
        photos={photos}
        renderImage={imageRenderer}
      />
    </div>
  );
}

MediaGallery.propTypes = {
  resources: PropTypes.arrayOf(PropTypes.object).isRequired,
  onInsert: PropTypes.func.isRequired,
  providerType: PropTypes.string.isRequired,
  canEditMedia: PropTypes.bool,
  isThumbnailBatchReady: PropTypes.bool,
  onThumbnailReady: PropTypes.func,
};

MediaGallery.defaultProps = {
  canEditMedia: false,
  isThumbnailBatchReady: false,
};

export default MediaGallery;
